import React, { useState, useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const PALETTE = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
  '#8B5CF6', '#6366F1', '#EC4899', '#14B8A6',
  '#F97316', '#9CA3AF'
];

const OTHER_COLOR = '#9CA3AF';

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const item = payload[0].payload;
    return (
      <div className="bg-gray-900/90 text-white text-xs px-2.5 py-2 rounded-lg shadow-xl backdrop-blur-sm pointer-events-none flex flex-col gap-1 whitespace-nowrap z-50">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
          <span className="font-medium text-gray-200">{item.name}:</span>
          <span className="font-bold text-white">
            {item.count} <span className="text-gray-300 font-normal">({item.percent}%)</span>
          </span>
        </div>
        {item.subItems && item.subItems.length > 0 && (
          <div className="text-[10px] text-gray-300 border-t border-gray-700/80 pt-1 mt-0.5 max-w-[220px] whitespace-normal leading-relaxed">
            <span className="text-gray-400 font-semibold">Gồm {item.subItems.length} lĩnh vực khác:</span>
            <div className="mt-0.5 text-gray-300">
              {item.subItems.map(s => `${s.name} (${s.count})`).join(', ')}
            </div>
          </div>
        )}
      </div>
    );
  }
  return null;
};

export default function CategoryPieChart({ data = [], isLoading = false, timeLabel = '' }) {
  const [activeIndex, setActiveIndex] = useState(null);

  const formattedData = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return [];

    // Sort categories descending by count (highest count first)
    const sorted = [...data]
      .map(item => ({
        ...item,
        count: Number(item.count) || 0,
        percent: Number(item.percent) || 0,
      }))
      .filter(item => item.count > 0)
      .sort((a, b) => b.count - a.count);

    if (sorted.length === 0) return [];

    const total = sorted.reduce((sum, item) => sum + item.count, 0);

    const MAX_TOP = 5;

    // If 5 or 6 total items, display all without grouping into "Khác"
    if (sorted.length <= MAX_TOP + 1) {
      return sorted.map((item, i) => {
        const pct = total > 0 ? Number(((item.count / total) * 100).toFixed(1)) : 0;
        return {
          ...item,
          percent: pct,
          color: item.color || PALETTE[i % PALETTE.length],
        };
      });
    }

    // Top 5 highest count categories
    const topItems = sorted.slice(0, MAX_TOP).map((item, i) => {
      const pct = total > 0 ? Number(((item.count / total) * 100).toFixed(1)) : 0;
      return {
        ...item,
        percent: pct,
        color: item.color || PALETTE[i % PALETTE.length],
      };
    });

    // Group remaining categories (the ones with fewest reports) into "Khác"
    const otherItems = sorted.slice(MAX_TOP);
    const otherCount = otherItems.reduce((sum, item) => sum + item.count, 0);
    const otherPct = total > 0 ? Number(((otherCount / total) * 100).toFixed(1)) : 0;

    topItems.push({
      name: 'Khác',
      count: otherCount,
      percent: otherPct,
      color: OTHER_COLOR,
      subItems: otherItems,
    });

    return topItems;
  }, [data]);

  const totalCount = useMemo(() => {
    return formattedData.reduce((sum, item) => sum + item.count, 0);
  }, [formattedData]);

  const activeItem = activeIndex !== null ? formattedData[activeIndex] : null;

  return (
    <div className="bg-white rounded-lg p-4 md:p-5 shadow-xs border border-gray-200 h-full flex flex-col justify-between overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs md:text-sm font-bold text-gray-800 uppercase tracking-wider">
          Phân loại phản ánh {timeLabel ? `(${timeLabel})` : ''}
        </h3>
        <span className="text-[11px] text-gray-400 font-medium">Cơ cấu loại</span>
      </div>

      {isLoading ? (
        <div className="flex-1 flex flex-col justify-between py-4 space-y-4">
          <div className="w-28 h-28 rounded-full border-4 border-gray-100 border-t-blue-400 animate-spin mx-auto my-2" />
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, idx) => (
              <div key={idx} className="h-4 bg-gray-100 rounded animate-pulse w-full" />
            ))}
          </div>
        </div>
      ) : formattedData.length === 0 ? (
        <div className="flex items-center justify-center flex-1 py-12 text-xs md:text-sm text-gray-400 font-medium">
          Chưa có dữ liệu phân loại
        </div>
      ) : (
        <>
          {/* Donut Chart with Center Text */}
          <div className="relative w-full h-[160px] flex items-center justify-center my-0.5">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={formattedData}
                  cx="50%"
                  cy="50%"
                  innerRadius={48}
                  outerRadius={68}
                  paddingAngle={3}
                  dataKey="count"
                  stroke="#ffffff"
                  strokeWidth={2}
                  onMouseEnter={(_, index) => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(null)}
                >
                  {formattedData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.color}
                      opacity={activeIndex === null || activeIndex === index ? 1 : 0.55}
                      className="transition-opacity duration-200 cursor-pointer"
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} wrapperStyle={{ outline: 'none', zIndex: 100 }} />
              </PieChart>
            </ResponsiveContainer>

            {/* Center Label (Dynamic on Hover) */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-2 text-center">
              {activeItem ? (
                <>
                  <span
                    className="text-xl font-extrabold leading-none transition-all duration-200"
                    style={{ color: activeItem.color }}
                  >
                    {activeItem.count}
                  </span>
                  <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider mt-0.5 truncate max-w-[85px]">
                    {activeItem.name}
                  </span>
                </>
              ) : (
                <>
                  <span className="text-xl font-extrabold text-gray-900 leading-none">
                    {totalCount}
                  </span>
                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mt-0.5">
                    PHẢN ÁNH
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Category Breakdown List */}
          <div className="space-y-1 pt-2 border-t border-gray-100/60 mt-1 max-h-[160px] overflow-y-auto pr-1">
            {formattedData.map((cat, i) => {
              const isSelected = activeIndex === i;
              return (
                <div
                  key={i}
                  onMouseEnter={() => setActiveIndex(i)}
                  onMouseLeave={() => setActiveIndex(null)}
                  className={`flex items-center justify-between py-0.5 px-1.5 rounded-lg transition-colors cursor-pointer ${
                    isSelected ? 'bg-gray-100/80 font-semibold' : 'hover:bg-gray-50/80'
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: cat.color }}
                    />
                    <span className="text-xs font-medium text-gray-700 truncate">{cat.name}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs flex-shrink-0">
                    <span className="font-bold text-gray-900">{cat.count}</span>
                    <span className="text-gray-400 font-normal">({cat.percent}%)</span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
